import { mergeObject } from "chameleon/lib/helpers/merge-object";
import {
  convertColorsToHSLA,
  generateDynamicColors,
  generateFontFamilies,
  generateFontWeights,
  generateLetterSpacings,
  generateLineHeights,
  generateSpacingUnit,
  pxFontSizesToRem,
} from "chameleon/lib/theme/helpers";
import {
  FontWeightKey,
  ThemeColor,
  TypographyKey,
} from "chameleon/lib/theme/theme";
import { Theme, ThemePartial, theme } from "chameleon/lib/theme/theme.css";

export const getMergedTheme = ({
  themeValueOverrides: originalThemeValueOverrides,
}: {
  themeValueOverrides: ThemePartial;
}): Theme => {
  // break these out to prevent pass by reference
  const themeValueOverrides = JSON.parse(
    JSON.stringify(originalThemeValueOverrides)
  ) as unknown as ThemePartial;
  const newTheme = JSON.parse(JSON.stringify(theme)) as unknown as Theme;

  const coreColors = {
    ...newTheme.color,
    ...convertColorsToHSLA((themeValueOverrides.color || {}) as ThemeColor),
  };
  themeValueOverrides.color = {
    ...coreColors,
    ...generateDynamicColors(coreColors),
  };
  themeValueOverrides.unit =
    themeValueOverrides?.unit ||
    generateSpacingUnit(
      themeValueOverrides.densityType || newTheme.densityType
    );
  // merge with defaults
  const remFontSizes = pxFontSizesToRem({
    ...newTheme.fontSizes,
    ...themeValueOverrides.fontSizes,
  });
  themeValueOverrides.fontSizes = remFontSizes;
  themeValueOverrides.letterSpacings = generateLetterSpacings({
    ...remFontSizes,
  } as Record<TypographyKey, string>);
  themeValueOverrides.lineHeights = generateLineHeights({
    ...remFontSizes,
  } as Record<TypographyKey, string>);
  themeValueOverrides.fontWeights = generateFontWeights({
    ...theme.fontWeights,
    ...themeValueOverrides.fontWeights,
  } as Record<TypographyKey, FontWeightKey>);

  const themeMerged = mergeObject<Theme>(newTheme, themeValueOverrides);

  if (!themeMerged.typography.fontSize.includes("px")) {
    themeMerged.typography.fontSize += "px";
  }
  if (!themeMerged.borderRadius.button.includes("px")) {
    themeMerged.borderRadius.button += "px";
  }
  if (!themeMerged.borderRadius.form.includes("px")) {
    themeMerged.borderRadius.form += "px";
  }

  themeMerged.fontFamilies = generateFontFamilies(
    themeValueOverrides.typography?.fontFamily ??
      newTheme.typography?.fontFamily,
    themeValueOverrides.fontFamilies
  );

  return themeMerged;
};
